import React, { useState, useEffect } from "react";
import { Button } from "@/components/button";
import { Input } from "@/components/input";
import { Fieldset, Legend } from "@/components/fieldset";
import { Text } from "@/components/text";
import type { 
  ICreateScoreConversionTableRequest,
  IUpdateScoreConversionTableRequest,
  IScaleToRawConversion,
  IRawScoreRange,
  IScoreConversionTable
} from "@/apis/score_conversion_tables/types";

interface ScoreConversionTableFormProps {
  initialData?: IScoreConversionTable | ICreateScoreConversionTableRequest;
  onSubmit: (data: ICreateScoreConversionTableRequest | IUpdateScoreConversionTableRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
  totalQuestions?: number;
  isEdit?: boolean;
}

interface RawScoreInput {
  value: string;
  error?: string;
}

const ScoreConversionTableForm: React.FC<ScoreConversionTableFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  totalQuestions = 0,
  isEdit = false
}) => {
  // Initialize form data with 36 scale scores (1-36)
  const [rawScoreInputs, setRawScoreInputs] = useState<Record<number, RawScoreInput>>(() => {
    const inputs: Record<number, RawScoreInput> = {};
    
    // Initialize all 36 scale scores
    for (let i = 1; i <= 36; i++) {
      const existingMapping = initialData?.scale_to_raw_mapping?.find(
        mapping => mapping.scale_score === i
      );
      
      let value = "";
      if (existingMapping?.raw_score) {
        if (existingMapping.raw_score.min === existingMapping.raw_score.max) {
          value = existingMapping.raw_score.min.toString();
        } else {
          value = `${existingMapping.raw_score.min}-${existingMapping.raw_score.max}`;
        }
      }
      
      inputs[i] = { value };
    }
    
    return inputs;
  });

  // Parse raw score input (handles single number, range, or empty)
  const parseRawScoreInput = (input: string): IRawScoreRange | null => {
    const trimmed = input.trim();
    
    if (!trimmed) {
      return null; // Empty input = no mapping
    }
    
    // Check if it's a range (e.g., "1-2" or "15-18")
    const rangeMatch = trimmed.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const min = parseInt(rangeMatch[1], 10);
      const max = parseInt(rangeMatch[2], 10);
      
      if (min > max) {
        throw new Error("Min value cannot be greater than max value");
      }
      
      return { min, max };
    }
    
    // Check if it's a single number
    const singleMatch = trimmed.match(/^\d+$/);
    if (singleMatch) {
      const value = parseInt(trimmed, 10);
      return { min: value, max: value };
    }
    
    throw new Error("Invalid format. Use single number (e.g., '5') or range (e.g., '1-2')");
  };

  // Get all raw scores that are covered by the current mappings
  const getCoveredRawScores = (): Set<number> => {
    const coveredScores = new Set<number>();
    
    Object.values(rawScoreInputs).forEach(input => {
      if (input.value.trim() && !input.error) {
        try {
          const range = parseRawScoreInput(input.value);
          if (range) {
            for (let i = range.min; i <= range.max; i++) {
              coveredScores.add(i);
            }
          }
        } catch (err) {
          // Skip invalid inputs
        }
      }
    });
    
    return coveredScores;
  };

  // Check for missing raw scores
  const getMissingRawScores = (): number[] => {
    if (totalQuestions === 0) return [];
    
    const coveredScores = getCoveredRawScores();
    const missingScores: number[] = [];
    
    // Check raw scores from 0 to totalQuestions
    for (let i = 0; i <= totalQuestions; i++) {
      if (!coveredScores.has(i)) {
        missingScores.push(i);
      }
    }
    
    return missingScores;
  };

  // Validate input and update state
  const handleInputChange = (scaleScore: number, value: string) => {
    let error: string | undefined;
    
    try {
      if (value.trim()) {
        const range = parseRawScoreInput(value);
        if (range && totalQuestions > 0) {
          // Check if raw scores are within valid range (0 to totalQuestions)
          if (range.min < 0 || range.max > totalQuestions) {
            error = `Raw scores must be between 0 and ${totalQuestions}`;
          }
        }
      }
    } catch (err) {
      error = err instanceof Error ? err.message : "Invalid input";
    }
    
    setRawScoreInputs(prev => ({
      ...prev,
      [scaleScore]: { value, error }
    }));
  };

  // Check if form has any errors
  const hasErrors = Object.values(rawScoreInputs).some(input => input.error);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (hasErrors) {
      return;
    }
    
    // Check for missing raw scores if totalQuestions is provided
    if (totalQuestions > 0) {
      const missingScores = getMissingRawScores();
      if (missingScores.length > 0) {
        const missingScoresText = missingScores.length > 10 
          ? `${missingScores.slice(0, 10).join(', ')} and ${missingScores.length - 10} more`
          : missingScores.join(', ');
        
        alert(`Missing raw scores: ${missingScoresText}\n\nPlease ensure all raw scores from 0 to ${totalQuestions} are covered.`);
        return;
      }
    }
    
    const scale_to_raw_mapping: IScaleToRawConversion[] = [];
    
    // Process all 36 scale scores
    for (let scaleScore = 1; scaleScore <= 36; scaleScore++) {
      const input = rawScoreInputs[scaleScore];
      let raw_score: IRawScoreRange | null = null;
      
      try {
        if (input.value.trim()) {
          raw_score = parseRawScoreInput(input.value);
        }
      } catch (err) {
        // This shouldn't happen due to validation, but just in case
        console.error(`Error parsing scale score ${scaleScore}:`, err);
        return;
      }
      
      scale_to_raw_mapping.push({
        scale_score: scaleScore,
        raw_score
      });
    }
    
    onSubmit({ scale_to_raw_mapping });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Fieldset>
        <div className="mt-4">
          {/* Show validation info if totalQuestions is provided */}
          {totalQuestions > 0 && (
            <div className="sticky top-4 z-10 mb-4 p-4 bg-white border border-blue-200 rounded-md shadow-md">
              <Text className="text-sm text-blue-800 font-medium mb-3">
                <strong>Raw Score Coverage (0-{totalQuestions}):</strong>
              </Text>
              
              {/* Visual representation of raw scores */}
              <div className="grid gap-1 mb-3" style={{gridTemplateColumns: 'repeat(auto-fill, minmax(32px, 1fr))'}}>
                {Array.from({ length: totalQuestions + 1 }, (_, i) => {
                  const coveredScores = getCoveredRawScores();
                  const isCovered = coveredScores.has(i);
                  
                  return (
                    <div
                      key={i}
                      className={`
                        w-8 h-8 flex items-center justify-center text-xs font-medium rounded border
                        ${isCovered 
                          ? 'bg-green-100 text-green-800 border-green-300' 
                          : 'bg-gray-100 text-gray-500 border-gray-300'
                        }
                      `}
                      title={`Raw Score ${i}: ${isCovered ? 'Covered' : 'Missing'}`}
                    >
                      {i}
                    </div>
                  );
                })}
              </div>
              
              {/* Summary text */}
              <div className="text-sm">
                {(() => {
                  const missingScores = getMissingRawScores();
                  const coveredCount = totalQuestions + 1 - missingScores.length;
                  
                  if (missingScores.length === 0) {
                    return (
                      <span className="text-green-700 font-medium">
                        ✓ All {totalQuestions + 1} raw scores are covered
                      </span>
                    );
                  }
                  
                  return (
                    <span className="text-red-600 font-medium">
                      {coveredCount}/{totalQuestions + 1} covered • {missingScores.length} missing
                    </span>
                  );
                })()}
              </div>
            </div>
          )}
          
          {/* Table Container with max width */}
          <div className="max-w-md mx-auto">
            {/* Table Header */}
            <div className="grid gap-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-t-md font-medium text-gray-700 text-sm" style={{gridTemplateColumns: "60px 1fr"}}>
              <div className="flex items-center justify-center">Scale</div>
              <div>Raw Score</div>
            </div>
            
            {/* Table Rows */}
            <div className="border-l border-r border-b border-gray-200 rounded-b-md">
              {Array.from({ length: 36 }, (_, index) => {
                const scaleScore = 36 - index; // 36부터 1까지 내림차순
                const input = rawScoreInputs[scaleScore];
                
                return (
                  <div key={scaleScore} className="grid gap-3 px-3 py-1.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50" style={{gridTemplateColumns: "60px 1fr"}}>
                    <div className="flex items-center justify-center font-medium text-gray-900 text-sm">
                      {scaleScore}
                    </div>
                    <div>
                      <Input
                        value={input.value}
                        onChange={(e) => handleInputChange(scaleScore, e.target.value)}
                        placeholder=""
                        className={`${input.error ? "border-red-500" : ""} text-sm`}
                      />
                      {input.error && (
                        <Text className="text-xs text-red-600 mt-0.5">
                          {input.error}
                        </Text>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Fieldset>

      <div className="flex justify-end gap-3">
        <Button type="button" onClick={onCancel} outline>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isLoading || hasErrors}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isLoading 
            ? (isEdit ? "Updating..." : "Creating...") 
            : (isEdit ? "Update Score Table" : "Create Score Table")
          }
        </Button>
      </div>
    </form>
  );
};

export default ScoreConversionTableForm; 